import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { AnimatePresence, motion } from "framer-motion";
import { Building2, X } from "lucide-react";

import type { CenterMembershipView } from "../../center";
import { useAdoptedTools, toolName, toolShortDescription, iconFor } from "../../marketplace";
import { useCompleteOnboarding } from "../../therapist";

interface Props {
    membership: CenterMembershipView;
}

/**
 * What a centre member sees instead of setting up a practice.
 *
 * There is nothing for them to set up: the centre decides their tools and
 * covers their plan. So instead of the tool picker, one screen that says so
 * and lists what the centre has switched on for them. Shown once, on the same
 * server-side flag as the practice onboarding, so it never comes back.
 */
export default function CenterWelcomeModal({ membership }: Props) {
    const { t } = useTranslation();
    const { data: tools, isLoading } = useAdoptedTools();
    const { completeOnboarding } = useCompleteOnboarding();
    const [isOpen, setIsOpen] = useState(true);
    const buttonRef = useRef<HTMLButtonElement>(null);

    const close = useCallback(() => {
        completeOnboarding();
        setIsOpen(false);
    }, [completeOnboarding]);

    useEffect(() => {
        if (!isOpen) return;
        buttonRef.current?.focus();
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") close();
        };
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, [isOpen, close]);

    const centerName = membership.center.name;

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    <motion.div
                        key="center-welcome-backdrop"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[9998] bg-black/60 backdrop-blur-sm"
                        onClick={close}
                    />
                    <motion.div
                        key="center-welcome"
                        initial={{ opacity: 0, scale: 0.95, y: 16 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 16 }}
                        transition={{ type: "spring", stiffness: 300, damping: 30 }}
                        className="pointer-events-none fixed inset-0 z-[9999] flex items-end justify-center p-4 sm:items-center"
                    >
                        <div
                            role="dialog"
                            aria-modal="true"
                            aria-labelledby="center-welcome-title"
                            // Capped to the viewport, with only the list
                            // scrolling: a long list must never push the
                            // button off the screen.
                            className="pointer-events-auto relative flex max-h-[90vh] w-[calc(100vw-2rem)] max-w-2xl flex-col rounded-3xl border border-gray-200/80 bg-surface-raised p-6 shadow-2xl dark:border-white/10 sm:p-8"
                        >
                            <button
                                type="button"
                                onClick={close}
                                aria-label={t("onboarding.close")}
                                className="absolute right-4 top-4 text-content-tertiary transition-colors hover:text-gray-600 dark:hover:text-gray-200"
                            >
                                <X className="h-5 w-5" />
                            </button>

                            <div className="flex min-h-0 flex-col items-center gap-4 text-center">
                                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-brand-500/10 text-brand-500">
                                    <Building2 className="h-6 w-6" />
                                </div>
                                <h3
                                    id="center-welcome-title"
                                    className="text-xl font-bold text-content-primary"
                                >
                                    {t("onboarding.center.title", { center: centerName })}
                                </h3>
                                <p className="text-sm leading-relaxed text-content-tertiary">
                                    {t("onboarding.center.message", { center: centerName })}
                                </p>

                                {!isLoading && (tools ?? []).length > 0 && (
                                    <ul className="custom-scrollbar grid min-h-0 w-full grid-cols-1 gap-2 overflow-y-auto text-left sm:grid-cols-2">
                                        {(tools ?? []).map((tool) => {
                                            const Icon = iconFor(tool.icon);
                                            return (
                                                <li
                                                    key={tool.id}
                                                    className="flex items-start gap-3 rounded-2xl border border-border-subtle px-3 py-2.5"
                                                >
                                                    <Icon className="mt-0.5 h-4 w-4 shrink-0 text-brand-500" />
                                                    <span className="min-w-0">
                                                        <span className="block text-sm font-semibold text-content-primary">
                                                            {toolName(t, tool)}
                                                        </span>
                                                        <span className="line-clamp-2 block text-xs leading-snug text-content-tertiary">
                                                            {toolShortDescription(t, tool)}
                                                        </span>
                                                    </span>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}

                                {!isLoading && (tools ?? []).length === 0 && (
                                    <p className="text-sm text-content-tertiary">
                                        {t("onboarding.center.noTools")}
                                    </p>
                                )}

                                {membership.is_admin && (
                                    <Link
                                        to="/center/tools"
                                        onClick={close}
                                        className="text-sm font-medium text-brand-600 hover:underline dark:text-brand-400"
                                    >
                                        {t("onboarding.center.manageLink")}
                                    </Link>
                                )}

                                <button
                                    ref={buttonRef}
                                    type="button"
                                    onClick={close}
                                    className="inline-flex w-full shrink-0 items-center justify-center rounded-xl bg-brand-500 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-brand-500/30 transition-colors hover:bg-brand-600"
                                >
                                    {t("onboarding.center.cta")}
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}
